// Product-analytics facade. A module singleton (not a React context) so it
// matches the existing trackUserAction module-fn pattern and can be called from
// non-React code such as the redux trackingMiddleware.
//
// `track(name, payload)` is type-checked against the generated catalog mirror
// (which cannot drift from the server catalog — CI enforces it). The call is
// fire-and-forget: it enqueues and returns immediately, never throws, and is
// suppressed under impersonation or when consent is withheld (the
// consent gate is a seam — see consent.ts).
//
// The React layer keeps the tracker's context (workspace / market /
// impersonation) in sync via setAnalyticsContext — see useAnalyticsContext in
// usePageViews.ts.

import { enqueue, flush, bindFlushListeners } from './queue';
import { getSessionId } from './session';
import { analyticsAllowed } from './consent';
import { normalizePath } from './routes';
import type { EventName, PayloadOf } from './generated/catalog';

interface AnalyticsContext {
  idClient: string | null;
  country: string | null;
  impersonating: boolean;
}

let ctx: AnalyticsContext = { idClient: null, country: null, impersonating: false };

/** Sync the ambient context (workspace / market / impersonation) from redux. */
export function setAnalyticsContext(next: Partial<AnalyticsContext>): void {
  ctx = { ...ctx, ...next };
}

export function track<N extends EventName>(
  eventType: N,
  payload: PayloadOf<N>,
  opts?: { page?: string },
): void {
  if (ctx.impersonating || !analyticsAllowed()) return;
  try {
    bindFlushListeners();
    enqueue({
      event_type: eventType,
      id_client: ctx.idClient,
      country: ctx.country,
      session_id: getSessionId(),
      // Default page is normalized + token-scrubbed so ANY caller (not just
      // page_viewed) is safe-by-construction — a raw id/token can't leak via page.
      page: opts?.page ?? (typeof window !== 'undefined' ? normalizePath(window.location.pathname) : null),
      props: (payload ?? {}) as Record<string, string | number | boolean>,
    });
  } catch {
    // Analytics must never break the app.
  }
}

/** Force a flush (used by tests / manual triggers; the queue also self-flushes). */
export const flushAnalytics = flush;
